import { useMemo, useContext } from 'react';

import { gql, useApolloClient } from '@apollo/client';
import { TableState } from 'react-table';

import { AuthContext } from 'auth';
import { useIsFeatureAvailable } from 'features';
import { Host } from 'hooks';

import { OPERATOR_HIDDEN_COLUMNS } from './contants';
import {
  IExtendedTableState,
  IReturnHostTablesStateValue,
  TSelectedRowsState,
} from './interfaces';

/**
 * Отвечает за управление состоянием таблиц хостов:
 * 1) выбранные хосты
 * 2) спрятанные колонки
 * @return {IReturnValue}
 */
export function useHostsTablesState(
  hasLockedHosts: boolean,
  selectedHosts: Array<number>
): IReturnHostTablesStateValue {
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const isBookingAvailable = useIsFeatureAvailable('booking');

  const { cache } = useApolloClient();

  const newSelectedRows = useMemo(() => {
    return selectedHosts.reduce<TSelectedRowsState>((acc, hostId) => {
      const groupId = cache.readQuery<{ group_id: number }>({
        id: cache.identify({ id: hostId, __typename: 'HostOverview' }),
        query: gql`
          query hostOverview {
            group_id
          }
        `,
      })?.group_id;

      if (!groupId) return acc;

      return {
        ...acc,
        [groupId]: {
          ...(acc[groupId] ?? {}),
          [hostId]: true,
        },
      };
    }, {});
  }, [selectedHosts]);

  const hiddenColumns = useMemo(() => {
    const newHiddenColumns = [];

    if (!permissions?.has('host_manage'))
      newHiddenColumns.push(...OPERATOR_HIDDEN_COLUMNS);

    if (!hasLockedHosts) newHiddenColumns.push('locked');

    if (!isBookingAvailable) newHiddenColumns.push('booking');

    return newHiddenColumns;
  }, [permissions, isBookingAvailable, hasLockedHosts]);

  const useControlledState = (state: TableState<Host>): TableState<Host> => {
    return useMemo(
      () => ({
        ...state,
        hiddenColumns,
        selectedRowIds:
          newSelectedRows[(state as IExtendedTableState).groupId] ?? {},
      }),
      [state, newSelectedRows, hiddenColumns]
    );
  };

  return {
    useControlledState,
  };
}
